import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, Hotel, CarFront, Camera, Star, Check, ArrowLeft, CalendarDays } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryDrawer from "@/components/EnquiryDrawer";
import { Reveal, Kicker } from "@/components/Reveal";
import { api, inr } from "@/api";

export default function PackageDetail() {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [packages, setPackages] = useState([]);
  const [missing, setMissing] = useState(false);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    setPkg(null);
    setMissing(false);
    api.get(`/packages/${id}`).then((r) => setPkg(r.data)).catch(() => setMissing(true));
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, [id]);

  if (missing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" data-testid="package-not-found">
        <h1 className="font-serif text-4xl text-slate-50">This journey has wandered off</h1>
        <Link to="/" className="mt-6 rounded-full bg-gold px-8 py-3 text-sm font-semibold text-onyx-900">Back to all packages</Link>
      </div>
    );
  }

  if (!pkg) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="package-loading">Composing your itinerary…</div>;
  }

  const inclusions = [
    { icon: Plane, title: "Flights", lines: [pkg.flight.airline, `${pkg.flight.route} · ${pkg.flight.duration}`, pkg.flight.class] },
    { icon: Hotel, title: "Stay", lines: [pkg.hotel.name, `${"★".repeat(pkg.hotel.stars)} · ${pkg.hotel.room}`, pkg.hotel.meals] },
    { icon: CarFront, title: "Transfers", lines: [pkg.transfers.type, pkg.transfers.details] },
    { icon: Camera, title: "Sightseeing", lines: pkg.sightseeing.slice(0, 4) },
  ];

  return (
    <div data-testid="package-detail-page">
      <Navbar onEnquire={() => setDrawer(true)} />

      <section className="relative h-[62vh] min-h-[26rem] overflow-hidden">
        <motion.img src={pkg.image} alt={pkg.name} className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }} />
        <div className="absolute inset-0 bg-gradient-to-t from-onyx-900 via-onyx-900/45 to-onyx-900/30" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            <Link to="/" data-testid="back-to-packages-link" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-300 hover:text-gold transition-colors">
              <ArrowLeft size={14} /> All packages
            </Link>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Kicker>{pkg.destination} · {pkg.nights} Nights / {pkg.days} Days</Kicker>
              {pkg.tag && <span className="rounded-full border border-gold/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{pkg.tag}</span>}
            </div>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-50 tracking-tight max-w-3xl" data-testid="package-title">{pkg.name}</h1>
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-300">
              <Star size={15} className="fill-gold text-gold" /> {pkg.rating} · {pkg.reviews} traveller reviews
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-16">
            <Reveal>
              <Kicker>The Journey</Kicker>
              <p className="mt-4 text-slate-300 leading-relaxed text-base sm:text-lg max-w-2xl" data-testid="package-overview">{pkg.overview}</p>
            </Reveal>

            <div>
              <Reveal><Kicker>What's Included</Kicker></Reveal>
              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                {inclusions.map((inc, i) => (
                  <Reveal key={inc.title} delay={i * 0.07}>
                    <div className="h-full rounded-2xl border border-white/10 bg-onyx-800 p-6 hover:border-gold/40 transition-colors" data-testid={`inclusion-${inc.title.toLowerCase()}`}>
                      <inc.icon size={22} className="text-gold" />
                      <h3 className="mt-3 font-serif text-lg text-slate-50">{inc.title}</h3>
                      <ul className="mt-2 space-y-1.5">
                        {inc.lines.filter(Boolean).map((l) => <li key={l} className="text-sm text-slate-400">{l}</li>)}
                      </ul>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <div>
              <Reveal><Kicker>Day by Day</Kicker></Reveal>
              <div className="mt-8 relative pl-8 space-y-8 before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-px before:bg-gradient-to-b before:from-gold/60 before:via-white/10 before:to-transparent" data-testid="itinerary-timeline">
                {pkg.itinerary.map((day, i) => (
                  <Reveal key={day.day} delay={i * 0.05}>
                    <div className="relative">
                      <span className="absolute -left-8 top-1 h-[19px] w-[19px] rounded-full border-2 border-gold bg-onyx-900 flex items-center justify-center">
                        <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                      </span>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Day {day.day}</div>
                      <h3 className="mt-1 font-serif text-xl text-slate-50">{day.title}</h3>
                      <p className="mt-1 text-sm text-slate-400 max-w-xl">{day.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Reveal delay={0.1}>
              <div className="lg:sticky lg:top-36 rounded-2xl border border-gold/25 bg-onyx-800 p-7" data-testid="booking-card">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">All-inclusive, per person</div>
                <div className="mt-2 flex items-baseline gap-3">
                  <span className="font-serif text-4xl text-gold" data-testid="package-price">{inr(pkg.price)}</span>
                  {pkg.old_price && <span className="text-sm text-slate-500 line-through">{inr(pkg.old_price)}</span>}
                </div>
                <ul className="mt-6 space-y-3 text-sm text-slate-300">
                  {["Return flights included", `${pkg.hotel.stars}-star stays with breakfast`, "Private transfers throughout", `${pkg.sightseeing.length} curated experiences`, "Visa guidance & 24×7 support"].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <Check size={15} className="text-gold mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
                <button onClick={() => setDrawer(true)} data-testid="package-enquire-btn"
                  className="mt-8 w-full rounded-full bg-gold py-4 text-sm font-semibold text-onyx-900 hover:bg-gold-light transition-colors">
                  Enquire about this trip
                </button>
                <p className="mt-4 flex items-center justify-center gap-2 text-[11px] text-slate-500">
                  <CalendarDays size={12} /> Flexible dates · free date change up to 30 days before departure
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Footer />
      <EnquiryDrawer open={drawer} pkg={pkg} packages={packages} onClose={() => setDrawer(false)} />
    </div>
  );
}
